import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { useTenantBranding } from "@/api/queries/branding";
import { AdminInsightNote } from "@/components/AdminInsightNote";
import { isPageVisible } from "@/shell/toggleablePages";

/**
 * KAN-1923 — what the shop's setup is missing, and where the owner fixes it.
 * ``href`` must be a real admin route (``TenantServiceShell``):
 *
 *   - ``noMaterials`` — no priced stock item for the operation → Sklad → Položky;
 *   - ``noFields``    — the operation declares no customer inputs → Procesy →
 *     Vlastní operace (a platform operation is fixed by the platform admin —
 *     the instruction says so).
 */
const ADMIN_FIX: Record<"noFields" | "noMaterials", string> = {
  noFields: "/admin/custom-operations",
  noMaterials: "/admin/materials",
};

interface Props {
  kind: "noFields" | "noMaterials";
  lang: string;
  /** The staged quote — rides along to the contact form like in
   *  ``QuoteStopActions``, so the shop knows which part the enquiry is about. */
  quoteSlug?: string;
  /** Signed-in admin of THIS shop (or platform admin). */
  isTenantAdmin: boolean;
  /** Disarms the unconfirmed-item guard before the customer leaves for the
   *  contact page (KAN-1650) — the part has to survive the way out. */
  onBeforeInquiry?: () => void;
  testId: string;
}

/**
 * KAN-1923 — the configurator's dead ends (operation without customer inputs,
 * operation without a priced material) used to tell the CUSTOMER to go to
 * „Admin → Ceník" — a screen they do not have, under a name the menu does not
 * use. The customer now gets a plain sentence and a way out (the shop's
 * contact page, when the shop shows one); the owner's instruction with a link
 * to the screen that fixes it renders only for a signed-in admin of the shop.
 */
export function ConfiguratorEmptyState({
  kind,
  lang,
  quoteSlug,
  isTenantAdmin,
  onBeforeInquiry,
  testId,
}: Props): JSX.Element {
  const { t } = useTranslation("quote");
  const branding = useTenantBranding();
  const showInquiry = isPageVisible(branding.data?.pages_visibility, "contact");
  const inquiryHref =
    `/${lang}/contact?topic=quote` +
    (quoteSlug ? `&ref=${encodeURIComponent(quoteSlug)}` : "");
  return (
    <div
      className="rounded-md border border-warn-border bg-warn-bg p-4 text-sm text-ink-2"
      data-testid={testId}
    >
      <p>{t(`dynamicPrimary.${kind}`)}</p>
      {showInquiry && (
        <a
          href={inquiryHref}
          onClick={onBeforeInquiry}
          data-testid={`${testId}-inquiry`}
          className="mt-3 inline-flex h-9 items-center rounded-md border border-border-strong bg-surface px-3 text-sm text-ink hover:bg-surface-2"
        >
          {t("dynamicPrimary.inquiry")}
        </a>
      )}
      {isTenantAdmin && (
        <AdminInsightNote
          insightKey={kind}
          href={ADMIN_FIX[kind]}
          lang={lang}
          testId={`${testId}-admin-insight`}
        />
      )}
    </div>
  );
}
